// KPI sektör paketleri (spec docs/superpowers/specs/2026-10-30-kpi-sektor-paketleri-design.md §3–§6).
// Paket = yalnız GLOBAL katalog göstergelerinin adlı kümesi; set kurulurken önceden işaretlenir.

import { z } from "zod";
import { SECTOR_CODES } from "../constants/sectors.js";

export const KPI_PACKAGE_STATUSES = ["active", "archived"] as const;
export type KpiPackageStatus = (typeof KPI_PACKAGE_STATUSES)[number];

export const KPI_PACKAGE_CODE_PREFIX = "pkt_";
/** `kpi_client_metric_units.unit` / `unitPreferences` ile aynı genişlik. */
export const KPI_PACKAGE_UNIT_MAX = 32;
/** Paket kaleminin kaynağı: elle seçim / AI önerisi / bağımlılık kapanışı (NULL = eski kayıt). */
export const KPI_PACKAGE_ITEM_SOURCES = ["manual", "ai", "auto"] as const;
export type KpiPackageItemSource = (typeof KPI_PACKAGE_ITEM_SOURCES)[number];
export const KPI_PACKAGE_SOURCE_REASON_MAX = 200;
/** Birlikte yükseltilen FORMÜL zinciri tavanı (kırılım grup/üyeleri sayılmaz — TODO-KPI-137 (2)). */
export const KPI_PROMOTE_CHAIN_MAX = 25;
/** Yükseltme gövdesindeki düğüm başına ad/kod/üst yaması tavanı (zincir + grup + üyeler). */
export const KPI_PROMOTE_OVERRIDES_MAX = 100;
export const KPI_PACKAGE_DEP_DEPTH_MAX = 10;

export const KPI_PACKAGE_ERROR_CODES = [
  "KPI_PACKAGE_STALE",
  "KPI_PACKAGE_ARCHIVED",
  "KPI_PACKAGE_CLIENT_REF",
  "KPI_PACKAGE_CATEGORY_ITEM",
  "KPI_PACKAGE_ORPHAN_MEMBER",
  "KPI_PACKAGE_UNIT_TEMPLATE",
  "KPI_PACKAGE_HAS_DEPRECATED",
  "KPI_PACKAGE_EMPTY",
  "KPI_PROMOTE_NOT_ROOT",
  "KPI_PROMOTE_MEMBER_CONFLICT",
  "KPI_PROMOTE_HAS_CLIENT_CHILDREN",
  "KPI_PROMOTE_CHAIN_TOO_LARGE",
  "KPI_PROMOTE_FORMULA_CLIENT_REFS",
  "KPI_SUGGEST_FAILED",
  "KPI_SUGGEST_TIMEOUT",
  "KPI_SUGGEST_BUSY",
  "KPI_SUGGEST_LLM_CONFIG",
  "KPI_SUGGEST_CATALOG_TOO_LARGE",
] as const;
export type KpiPackageErrorCode = (typeof KPI_PACKAGE_ERROR_CODES)[number];

const Uuid = z.string().uuid();
const Name = z.string().trim().min(1).max(200);
const SectorCodeField = z.enum(SECTOR_CODES);

export const KpiPackageSchema = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  sectorCode: z.string().nullable(),
  frameworkId: Uuid.nullable(),
  status: z.enum(KPI_PACKAGE_STATUSES),
  orderIndex: z.number().int(),
  itemCount: z.number().int().nonnegative(),
  autoAddedCount: z.number().int().nonnegative(),
  deprecatedCount: z.number().int().nonnegative(),
  missingMemberCount: z.number().int().nonnegative(),
  usedBySetCount: z.number().int().nonnegative(),
  /** µs duyarlı metin (iyimser kilit jetonu; `expectedUpdatedAt` olarak geri gönderilir). */
  updatedAt: z.string(),
  createdAt: z.string(),
});
export type KpiPackage = z.infer<typeof KpiPackageSchema>;

export const KpiPackageItemSchema = z.object({
  metricId: Uuid,
  orderIndex: z.number().int(),
  unitDefault: z.string().max(KPI_PACKAGE_UNIT_MAX).nullable(),
  activeFormulaId: Uuid.nullable(),
  showChange: z.boolean().nullable(),
  autoAdded: z.boolean(),
  source: z.enum(KPI_PACKAGE_ITEM_SOURCES).nullable(),
  sourceReason: z.string().nullable(),
  addedAt: z.string(),
  updatedAt: z.string(),
});
export type KpiPackageItem = z.infer<typeof KpiPackageItemSchema>;

export const KpiPackageCreateSchema = z.object({
  code: z.string().trim().min(1).max(64).optional(),
  name: Name,
  description: z.string().max(2000).nullable().optional(),
  sectorCode: SectorCodeField.nullable().optional(),
  frameworkId: Uuid.nullable().optional(),
});
export type KpiPackageCreate = z.infer<typeof KpiPackageCreateSchema>;

export const KpiPackageUpdateSchema = KpiPackageCreateSchema.omit({ code: true })
  .partial()
  .extend({ status: z.enum(KPI_PACKAGE_STATUSES).optional() });
export type KpiPackageUpdate = z.infer<typeof KpiPackageUpdateSchema>;

/** Şablon `{kod}` yalnız türetilmiş göstergenin katalog birimidir; paket varsayılanında yasak. */
export const KpiPackageItemInputSchema = z.object({
  metricId: Uuid,
  unitDefault: z
    .string()
    .max(KPI_PACKAGE_UNIT_MAX)
    .refine((v) => !v.includes("{"), { message: "KPI_PACKAGE_UNIT_TEMPLATE" })
    .nullable()
    .optional(),
  activeFormulaId: Uuid.nullable().optional(),
  showChange: z.boolean().nullable().optional(),
  /** İstemci yalnız manual/ai yazar; "auto" sunucu kapanışına aittir. */
  source: z.enum(["manual", "ai"]).optional(),
  sourceReason: z.string().trim().max(KPI_PACKAGE_SOURCE_REASON_MAX).nullable().optional(),
});
export type KpiPackageItemInput = z.infer<typeof KpiPackageItemInputSchema>;

export const KpiPackageItemsBodySchema = z.object({
  items: z.array(KpiPackageItemInputSchema).max(2000),
  expectedUpdatedAt: z.string().min(1),
});
export type KpiPackageItemsBody = z.infer<typeof KpiPackageItemsBodySchema>;

export const KpiPackageItemsResultSchema = z.object({
  added: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
  autoAdded: z.array(z.object({ metricId: Uuid, requiredBy: z.array(z.string()) })),
  updatedAt: z.string(),
});
export type KpiPackageItemsResult = z.infer<typeof KpiPackageItemsResultSchema>;

export const KpiSetBulkAddBodySchema = z
  .object({
    packageIds: z.array(Uuid).max(50).optional(),
    metricIds: z.array(Uuid).max(2000).optional(),
  })
  .refine((b) => (b.packageIds?.length ?? 0) + (b.metricIds?.length ?? 0) > 0, {
    message: "packageIds ya da metricIds en az biri dolu olmalı",
  });
export type KpiSetBulkAddBody = z.infer<typeof KpiSetBulkAddBodySchema>;

export const KpiSetBulkAddResultSchema = z.object({
  added: z.number().int().nonnegative(),
  existing: z.number().int().nonnegative(),
  ancestorsAdded: z.number().int().nonnegative(),
  unresolvedFormulaRefs: z.array(
    z.object({ metricId: Uuid, code: z.string(), missing: z.array(z.string()) }),
  ),
});
export type KpiSetBulkAddResult = z.infer<typeof KpiSetBulkAddResultSchema>;

export const KpiPackageFromSetBodySchema = z.object({
  name: Name,
  sectorCode: SectorCodeField.nullable().optional(),
});
export type KpiPackageFromSetBody = z.infer<typeof KpiPackageFromSetBodySchema>;

/** Promote üst kategori sorunları: yok / bulunamadı / kategori değil / müşteriye özel. */
export const KPI_PROMOTE_PARENT_PROBLEMS = [
  "missing",
  "not_found",
  "not_category",
  "not_global",
] as const;
export type KpiPromoteParentProblem = (typeof KPI_PROMOTE_PARENT_PROBLEMS)[number];

const PromoteChainItemSchema = z.object({
  metricId: Uuid,
  label: Name.optional(),
  newCode: z.string().trim().min(1).max(64).optional(),
  parentId: Uuid.optional(),
});

export const KpiPromoteBodySchema = z.object({
  label: Name.optional(),
  newCode: z.string().trim().min(1).max(64).optional(),
  /** Önizlemede (dryRun) boş olabilir → `parentProblem: "missing"`; yazımda zorunlu (400). */
  parentId: Uuid.optional(),
  packageIds: z.array(Uuid).max(50).optional(),
  // TODO-KPI-137 (2): zincir sayımı sunucuda yalnız formül zinciri (`KPI_PROMOTE_CHAIN_MAX`); gövde
  // kırılım üyelerinin yamalarını da taşır → ayrı, geniş tavan
  chain: z.array(PromoteChainItemSchema).max(KPI_PROMOTE_OVERRIDES_MAX).optional(),
  dryRun: z.boolean().optional(),
});
export type KpiPromoteBody = z.infer<typeof KpiPromoteBodySchema>;

export const KpiPromotePreviewSchema = z.object({
  chain: z.array(
    z.object({
      metricId: Uuid,
      code: z.string(),
      label: z.string(),
      newCode: z.string(),
      parentId: Uuid.nullable(),
    }),
  ),
  setFormulas: z.number().int().nonnegative(),
  catalogFormulas: z.number().int().nonnegative(),
  unitTemplates: z.number().int().nonnegative(),
  nameWarnings: z.array(z.object({ metricId: Uuid, label: z.string() })),
  suggestedParentId: Uuid.nullable(),
  suggestedPackageIds: z.array(Uuid),
  /** Üst kategori sorunu (DR9 açılış önizlemesi atmaz); null = üst geçerli. */
  parentProblem: z.enum(KPI_PROMOTE_PARENT_PROBLEMS).nullable().optional(),
  /** Zincirdeki TÜM geçersiz üstler (satır bazlı gösterim); `parentProblem` ilkine eşittir. */
  parentProblems: z
    .array(z.object({ metricId: Uuid, problem: z.enum(KPI_PROMOTE_PARENT_PROBLEMS) }))
    .optional(),
});
export type KpiPromotePreview = z.infer<typeof KpiPromotePreviewSchema>;
